import type { TrackStatus } from "@engine/round/aggregate";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  LayoutDashboard,
  Database,
  Download,
  Eye,
  GitBranch,
  History,
  Settings,
  ShieldCheck,
  Users,
  VenetianMask,
} from "lucide-react";

const TRACK_PREVIEW_LABEL = "Preview as Track";

export const PHASE_RESTRICTED: TrackStatus[] = [
  "3.full-discussion",
  "4.evals-freeze",
  "5.complete",
];

export type RoundAdminNavItemDef = {
  label: string;
  icon: LucideIcon;
  href: string;
  phases?: TrackStatus[];
  // Also shown to funders; items default to round admins only.
  funderVisible?: boolean;
  separatorBefore?: boolean;
};

export function buildRoundAdminOrFunderNavItems(
  roundId: string,
  forNestedRoundRouter = false,
  isSfcRound = true,
  usesTracks = true,
  allowsAnonymization = true,
): RoundAdminNavItemDef[] {
  const inRound = (path: string) =>
    forNestedRoundRouter ? path : `/round/${roundId}${path}`;

  // Both round types can rename the round; SFC rounds use a dedicated
  // rename-only page, while non-SFC rounds use their full settings page.
  const roundSettingsItem: RoundAdminNavItemDef = {
    label: "Round Settings",
    icon: Settings,
    href: inRound(isSfcRound ? "/admin/round-settings" : "/settings/round"),
  };

  // Non-SFC rounds don't have the SFC admin surfaces (track settings, members,
  // proposal forms, etc.), so their only admin nav entry is Round Settings.
  // Listing the SFC-only items here would point them at routes that 404.
  if (!isSfcRound) {
    return [roundSettingsItem];
  }

  return [
    {
      label: "Round Dashboard",
      icon: LayoutDashboard,
      href: inRound("/admin/dashboard"),
    },
    {
      label: "Recommender Activity",
      icon: Activity,
      href: inRound("/admin/recommender-activity"),
    },
    {
      label: "Fact-Check Responses",
      icon: ShieldCheck,
      href: inRound("/admin/fact-check-responses"),
    },
    {
      label: "Members",
      icon: Users,
      href: inRound("/admin/round-members"),
    },
    {
      // The page manages per-track phase progression; a trackless round has
      // only the MAIN track, so the entry drops the track vocabulary.
      label: usesTracks ? "Track Settings" : "Phase Settings",
      icon: GitBranch,
      href: inRound("/admin/track-settings"),
    },
    {
      label: "Data Syncing",
      icon: Database,
      href: inRound("/admin/data-syncing"),
    },
    // Name anonymization is track-based, so in a trackless round the page
    // manages nothing (every member sees real names) — the entry is dropped
    // rather than offering switches with no effect. The page itself explains
    // this for anyone landing on a stale deep link.
    ...(usesTracks && allowsAnonymization
      ? [
          {
            label: "Name Visibility",
            icon: Eye,
            href: inRound("/admin/anonymization"),
          },
        ]
      : []),
    {
      label: "Logs",
      icon: History,
      href: inRound("/admin/logs"),
    },
    {
      label: "Export Data",
      icon: Download,
      phases: PHASE_RESTRICTED,
      funderVisible: true,
      href: inRound("/export-data"),
    },
    {
      label: "Settlements",
      icon: Download,
      href: inRound("/speculation-grants"),
    },
    roundSettingsItem,
    {
      label: TRACK_PREVIEW_LABEL,
      icon: VenetianMask,
      href: inRound("/admin/impersonate"),
      separatorBefore: true,
    },
  ];
}

export function isRoundAdminNavItemVisible(
  item: Pick<RoundAdminNavItemDef, "phases" | "funderVisible">,
  ctx: {
    isAdmin: boolean;
    isFunder: boolean;
    currentPhases: TrackStatus[] | null;
  },
): boolean {
  const roleOk = ctx.isAdmin || (item.funderVisible && ctx.isFunder);
  if (!roleOk) return false;
  if (!item.phases) return true;
  if (ctx.currentPhases == null) return false;
  if (ctx.currentPhases.length === 0 && ctx.isAdmin) return true;
  return item.phases.some((p) => ctx.currentPhases!.includes(p));
}

export function resolveRoundAdminNavBrowserPath(
  roundId: string,
  linkHref: string,
  forNestedRoundRouter: boolean,
): string {
  if (linkHref.startsWith("~/")) {
    return linkHref.slice(1);
  }
  if (!forNestedRoundRouter) {
    return linkHref;
  }
  return `/round/${roundId}${linkHref}`;
}

export function adminNavItemIsActive(
  pathname: string,
  roundId: string,
  linkHref: string,
  forNestedRoundRouter: boolean,
): boolean {
  if (forNestedRoundRouter) {
    const prefix = `/round/${roundId}`;
    const nestedPath =
      pathname === prefix || pathname.startsWith(`${prefix}/`)
        ? pathname === prefix
          ? "/"
          : pathname.slice(prefix.length)
        : pathname;
    return nestedPath === linkHref || nestedPath.startsWith(`${linkHref}/`);
  }
  const target = resolveRoundAdminNavBrowserPath(
    roundId,
    linkHref,
    forNestedRoundRouter,
  );
  return pathname === target || pathname.startsWith(`${target}/`);
}
